import { normalizeStaticFullscreenType } from 'Editor/utils/fullscreenType';
import { Button } from "@/components/ui/button";
import MyRefetchButton from "components/ui/MyRefetchButton";
import { toast } from "@/components/ui/use-toast";
import { fetcher, postFetcher } from "api/swr";
import { withCacheBust } from "commons/cdn";
import { useEditorStore } from "CanvasCore/uiSlice";
import { captureCanvasAvatarPayload } from "Editor/utils/captureCanvasAvatarPayload";
import { createMixedSpineAvatar } from "Editor/utils/createMixedSpineAvatar";
import { useUI } from "Editor";
import { useConfigStore } from "AdminPanel/Spine/store/configStore";
import { useEditorStore as useSpineEditorStore } from "AdminPanel/Spine/store/editorStore";
import { ImageUploadProgressDialog } from "AdminPanel/Spine/editor/create-avatar/ImageUploadProgressDialog";
import { ExportAnimationProgressDialog } from "AdminPanel/Spine/editor/export/components/export-animation/ExportAnimationProgressDialog";
import { shouldUseAvatarSpinePreview } from "Editor/lib/avatarSpinePreview";
import { Loader2, Package } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { AvatarCard } from "./AvatarCard";
import { DefaultSetGotoEditorMenu } from "./DefaultSetGotoEditorMenu";
import { RemoveDefaultSetAvatarButton } from "./RemoveDefaultSetAvatarButton";

const bustOutfitMedia = (avatar: any, t: number) => ({
  ...avatar,
  url: withCacheBust(avatar.url, t),
  thumb: withCacheBust(avatar.thumb, t),
  videoUrl: withCacheBust(avatar.videoUrl, t),
  videoPreviewUrl: withCacheBust(avatar.videoPreviewUrl, t),
});

const DefaultSetPanel: FC = () => {
  const { tid } = useParams<{ tid: string }>();
  const { data: template } = useSWR(tid ? [`/templates/${tid}`] : null, fetcher);
  const drawRef = useUI((s) => s.canvasDrawRef);
  const charDrawRef = useUI((s) => s.canvasCharDrawRef);
  const avatarSpineExport = useUI((s) => s.avatarSpineExport);
  const loadedRelatedTemplates = useConfigStore((s) => s.loadedRelatedTemplates);
  const allRelatedTemplateIds = useConfigStore((s) => s.allRelatedTemplateIds);
  const selectedSkins = useSpineEditorStore((s) => s.selectedSkins);
  const selectedToningColors = useSpineEditorStore((s) => s.selectedToningColors);
  const currentAnimation = useSpineEditorStore((s) => s.currentAnimation);
  const isMixed = shouldUseAvatarSpinePreview(
    selectedSkins,
    loadedRelatedTemplates,
  );
  const stage = useEditorStore((s) =>
    tid ? s.editors[tid]?.stage : undefined,
  );
  const fullscreenType = normalizeStaticFullscreenType(template?.fullscreenType);
  const [pending, setPending] = useState(false);
  const [exportProgressOpen, setExportProgressOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [uploadProgressOpen, setUploadProgressOpen] = useState(false);
  const [mediaBust, setMediaBust] = useState(0);

  const sortedRelatedTemplates = useMemo(() => {
    const orderIndex = new Map<string, number>();
    (allRelatedTemplateIds || []).forEach((id, i) => orderIndex.set(id, i));
    return [...(loadedRelatedTemplates || [])].sort(
      (a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0),
    );
  }, [loadedRelatedTemplates, allRelatedTemplateIds]);

  const listKey =
    tid != null && tid !== "" ? ["/avatars", { tid, defaultSet: 1 }] : null;

  const {
    data: outfits = [],
    isLoading,
    mutate,
    isValidating,
  } = useSWR<any[]>(listKey, fetcher);

  const displayOutfits = useMemo(
    () => (mediaBust ? outfits.map((item) => bustOutfitMedia(item, mediaBust)) : outfits),
    [outfits, mediaBust],
  );

  const refreshList = useCallback(() => {
    setMediaBust(Date.now());
    if (!tid) return;
    void mutate(
      () => fetcher(["/avatars", { tid, defaultSet: 1 }, { cache: "no-store" }]),
      { revalidate: false },
    );
  }, [mutate, tid]);

  const handleSaveAsDefaultSet = async () => {
    if (!tid) {
      toast({
        variant: "destructive",
        title: "Failed",
        description: "Missing template ID",
      });
      return;
    }

    if (isMixed) {
      if (!stage?.h) {
        toast({
          variant: "destructive",
          title: "Failed",
          description: "Cannot capture canvas, please confirm the canvas is loaded",
        });
        return;
      }
      setPending(true);
      setExportProgressOpen(true);
      setExportProgress(0);
      try {
        await createMixedSpineAvatar({
          tid,
          stage,
          drawRef,
          charDrawRef,
          relatedTemplates: sortedRelatedTemplates,
          selectedSkins,
          selectedToningColors,
          currentAnimation,
          avatarSpineExport,
          defaultSet: 1,
          fullscreenType,
          onExportProgress: setExportProgress,
          onUploadStart: () => {
            setExportProgressOpen(false);
            setExportProgress(0);
            setUploadProgressOpen(true);
          },
        });
        setMediaBust(Date.now());
        await mutate();
        toast({
          title: "已设为套装",
          description: "Avatar 图片生成大约需要 10 秒，之后点刷新即可看到。",
          className: "bg-green-500 text-white",
        });
      } catch (err: unknown) {
        toast({
          variant: "destructive",
          title: "Failed",
          description: err instanceof Error ? err.message : "Unknown error",
        });
      } finally {
        setExportProgressOpen(false);
        setExportProgress(0);
        setUploadProgressOpen(false);
        setPending(false);
      }
      return;
    }

    const captured = captureCanvasAvatarPayload(
      tid,
      stage,
      drawRef,
      fullscreenType,
    );
    if (!captured) {
      toast({
        variant: "destructive",
        title: "Failed",
        description: "Cannot capture canvas, please confirm the canvas is loaded",
      });
      return;
    }
    if ("error" in captured && captured.error === "draft") {
      toast({
        variant: "destructive",
        title: "Failed",
        description: "Cannot use draft layer to upload",
      });
      return;
    }
    const { drawData } = captured as {
      drawData: any;
    };

    setPending(true);
    try {
      await postFetcher("/avatars", {
        arg: {
          avatar: `${tid}:${Date.now()}`,
          tid,
          drawData,
          defaultSet: 1,
          fullscreenType,
        },
      });
      setMediaBust(Date.now());
      await mutate();
      toast({
        title: "已设为套装",
        description: "Avatar 图片生成大约需要 10 秒，之后点刷新即可看到。",
        className: "bg-green-500 text-white",
      });
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setPending(false);
    }
  };

  const disabled = !tid || !stage?.h || !drawRef?.current || pending;

  return (
    <div className="w-full space-y-4 mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="default"
          className="gap-1.5"
          disabled={disabled}
          onClick={() => void handleSaveAsDefaultSet()}
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Package className="h-4 w-4" />
          )}
          设置当前搭配为套装
        </Button>
        <MyRefetchButton
          disabled={!tid || isValidating}
          onClick={refreshList}
        />
      </div>

      <ExportAnimationProgressDialog
        open={exportProgressOpen}
        format="webp"
        progress={exportProgress}
        title={
          isMixed
            ? "正在处理混合套装（静态图或 VAP）"
            : "正在生成 VAP 视频（512 + 304）"
        }
      />
      <ImageUploadProgressDialog open={uploadProgressOpen} />

      <div className="space-y-2">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : displayOutfits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No outfits</p>
        ) : (
          <div className="flex flex-col gap-3">
            {displayOutfits.map((avatar: any) => (
              <AvatarCard
                key={mediaBust ? `${avatar.id}-${mediaBust}` : avatar.id}
                avatar={avatar}
                actionsSlot={
                  <div className="flex items-center gap-1.5">
                    {tid ? (
                      <DefaultSetGotoEditorMenu
                        avatar={avatar}
                        tid={tid}
                        onPinned={() => void mutate()}
                      />
                    ) : null}
                    <RemoveDefaultSetAvatarButton
                      avatarId={avatar.id}
                      onRemoved={() => void mutate()}
                    />
                  </div>
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DefaultSetPanel;
